import React, { useState } from 'react'
import { Box, useTheme } from '@mui/material'
import { keyframes } from '@emotion/react'

import { PoweredByVera } from './PoweredByVera'

/**
 * The in-app continuation of the scan loader.
 *
 * This is the SAME visual as the CSS-only loader painted by the entry documents
 * (canonical: `packages/ui/scan-loader/scan-loader.html`, inlined into
 * apps/app/index.html and widget-installation.hbs). Keeping them identical is the
 * point: the app takes over inside the same document, so any difference in ring
 * size, mark, colour or position would read as the page restarting rather than
 * still loading.
 *
 * It deliberately has NO entrance delay, unlike the HTML version. That version
 * hides itself for the first 300ms so a fast scan never flashes a loader; by the
 * time React has mounted and is fetching, that window is long past, so fading in
 * again would itself be the flicker the delay exists to prevent.
 *
 * ⚠️ THE "NOTHING RENDERS THIS" WARNING IS SPENT — `App` renders it as of VRG-529, on
 * both loading branches, replacing the bare `<CircularProgress>` that used to sit
 * there. Behaviour added here is now reachable. What has NOT changed is the shape
 * caveat below, which still qualifies the "SAME visual" claim above:
 *   - The claim is only partly true. The CSS loader draws a faint full TRACK plus a
 *     fixed 36/96 dash ARC over it; this draws a single circle with an animated
 *     dashoffset, which is the shape that version was explicitly moved off. Sizes are
 *     kept in step (both stages are 64px), the ring's motion is not.
 *
 * ⚠️ WHEN THIS IS SEEN IS A DIFFERENT QUESTION FROM WHETHER IT IS MOUNTED, AND THE
 * HONEST ANSWER IS: NOT ON A NORMAL SCAN. Say it plainly rather than let the branding
 * above imply otherwise.
 *
 * The two conditions are exact complements by construction. This component is mounted
 * iff `isCompanyThemeLoading || isLoading` (App's early return, then the ternary the
 * early return guards). `App`'s dismissal effect drops the pre-boot CSS cover iff
 * `!isLoading && !isCompanyThemeLoading`, and that dismissal is one-way — the module
 * latches a `dismissed` flag, and neither flag is ever re-raised after boot
 * (`setIsLoading(true)` occurs only inside the mount effect, before any settle). So on
 * both host documents that carry the snippet — apps/app/index.html and the widget body
 * from widget-installation.hbs — this renders UNDERNEATH the cover for its whole life
 * and the user never sees it.
 *
 * It is reachable, not dead, in exactly three cases, all of them fallbacks:
 *   - after the cover's 8s CSS failsafe hides it (a pathologically slow load, which is
 *     also the load where a bare unstyled spinner would land worst);
 *   - the legacy `vera-widget-preload-overlay` markup, removed outright rather than
 *     faded;
 *   - any host document that does not carry the snippet at all.
 *
 * So the epic's "branded loading" is NOT delivered here on the main path. It is
 * delivered upstream, by VRG-534's server-rendered interstitial at `GET /verify/:id`,
 * which wears the brand from the first byte and covers the window this component
 * cannot reach. What this component is worth keeping for is narrower and still real:
 * the three cases above used to render a bare, structurally-unthemable
 * `<CircularProgress>`, and now render the same mark, ring and size the cover was
 * showing, so the handover has no visual jump. Do not upgrade that into a claim that
 * the scan loader is branded.
 *
 * ⚠️ VERA-304 widens that gap DELIBERATELY: `logoUrl` lets this loader show the TENANT'S
 * mark, and the CSS loader structurally cannot. It is painted by a static document that
 * is byte-identical for every brand and runs before any fetch, so on a cold visit it has
 * no way to know whose page it is on. That asymmetry is not drift to be reconciled — it
 * is the one thing post-boot can do that pre-boot cannot, and it is why this component is
 * being kept for B2C rather than folded into the CSS one.
 *
 * ⚠️ This component previously ran a hard-coded 2000/2800/3800ms GIF->mark->wordmark
 * timeline and only released via an `onComplete` callback. Because its callers gated
 * on that callback rather than on their own data, EVERY anonymous scan was held for
 * 3.8 seconds no matter how fast the certificate loaded -- and InvalidScans made the
 * user wait 3.8s to be told their scan was invalid. The timeline and the callback are
 * both gone; callers now gate on real loading state. Do not reintroduce a timer here.
 */

const spin = keyframes`to { transform: rotate(360deg); }`
const dash = keyframes`
  0%   { stroke-dashoffset: 128; }
  50%  { stroke-dashoffset: 34; }
  100% { stroke-dashoffset: 128; }
`
const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`

interface LogoLoaderProps {
  /**
   * Shown under the mark. Appears after 1.2s, matching the HTML loader.
   *
   * Defaults to the snippet's own neutral "Loading…" rather than to the cover's
   * `vt`-upgraded copy, and that asymmetry is deliberate. `upgradeScanLoaderLabel`
   * may say "Verifying…" (VRG-825) because it has checked for a `vt` on the entry
   * URL, and `vt` is only ever minted on the verify controller's success redirect —
   * so it KNOWS a scan token is being verified and a certificate is what loads. This
   * component has no such guarantee: it also renders on plain app boots and
   * shared-link opens, where nothing was scanned. Re-reading the URL here to
   * recover the guarantee would put a second copy of that rule in a presentational
   * component, which is how the two halves drift; a caller that knows better passes
   * `label`.
   *
   * It used to default to "Authenticating…", which was wrong on the dominant path
   * for the reason `utils/scanLoader` records: an anonymous NFC tap has no stored
   * session, so the auth check finds nothing and moves on, and the word names a
   * user-account operation that is not happening.
   */
  label?: string
  /**
   * The tenant's own mark, shown INSTEAD of the Vera V (VERA-304).
   *
   * Comes from the theme — `useGetCompanyStyles().styles.logo`, merged server-side
   * from `companies.logo` (see `settings/mui/palette.ts`, `Theme.logo`). Omitted
   * when the brand has no logo, in which case the V is what renders.
   *
   * Only the POST-BOOT loader can take this: the pre-boot CSS loader is a static
   * document served identically to every brand and cannot fetch, so it keeps the V.
   * The policy on WHICH brands get a logo at all lives in exactly one place, the API
   * (`get-company-style-settings.use-case.ts`, BRAND_LOGO_REQUIRES_WHITE_LABEL_PLAN);
   * this component renders whatever it is given and decides nothing.
   */
  logoUrl?: string
}

/** The Vera mark, at the mark box's own 172x148 artboard. */
const VERA_MARK_PATH =
  'M84.0345 79.33L39.6849 1.77528C39.3364 1.17763 38.6829 0.802368 37.9568 0.802368H2.39299C0.897245 0.802368 -0.0466709 2.34512 0.679419 3.59601L84.0345 147.031C84.7896 148.323 86.721 148.323 87.4616 147.031L170.817 3.59601C171.543 2.34512 170.599 0.802368 169.103 0.802368H133.539C132.828 0.802368 132.16 1.17763 131.811 1.77528L87.4616 79.33C86.721 80.6365 84.7606 80.6365 84.02 79.33H84.0345Z'

/**
 * The mark's box, shared by the V and by a brand logo so the two are
 * interchangeable without the ring appearing to resize. These are loader.css's
 * `.vera-scan-loader__mark` numbers — the original 19x17 scaled by the same 4/3
 * the stage took when it went 48 -> 64. Change them together with that file.
 */
const MARK_BOX = { width: 25.33, height: 22.67 }

/**
 * The ring and V colour when no brand colour is configured — loader.css's own
 * `stroke: #272727` and the `fill='%23272727'` baked into its mark data-URI.
 *
 * It is the FALLBACK, not the default, and the distinction is the whole point of
 * `resolveMarkColor` below.
 */
const FALLBACK_MARK_COLOR = '#272727'

/**
 * Brand accent, or the pre-boot cover's own grey — never Vera's purple.
 *
 * ⚠️ `theme.palette.primary.main` cannot answer this on its own, and reading it
 * unguarded is the trap. `settings/mui/palette.ts` resolves it as
 * `theme?.primary_color || basePalette.brand[600]`, so it is NEVER undefined: a brand
 * with no colour configured gets Vera's purple, and a `primary.main ?? '#272727'`
 * would leave the fallback permanently unreachable.
 *
 * `palette.brand.isTenantDerived` is the signal that CAN answer it, and it exists for
 * exactly this reason — `settings/mui/brandRamp.ts` publishes it because "no brand
 * colour configured" and "a brand whose colour is black" are otherwise the same eleven
 * strings once the ramp is built. `CountBadge` is the other consumer, and reads it the
 * same way (including the `as any`: `brand` is not in the MUI `Palette` augmentation).
 *
 * Falling back to grey rather than purple is a CONTINUITY decision, not a preference.
 * This loader takes over inside the same document the CSS cover painted, and that cover
 * is #272727 for everyone because it is a static file served before any fetch. An
 * unbranded scan that went #272727 -> purple mid-load would read as the page
 * restarting, which is the failure this whole component is written around. A brand that
 * HAS set a colour gets it, which is the case the change exists for.
 */
const resolveMarkColor = (palette: { primary?: { main?: string } }): string => {
  const brand = (palette as any).brand
  return brand?.isTenantDerived ? palette.primary?.main || FALLBACK_MARK_COLOR : FALLBACK_MARK_COLOR
}

export const LogoLoader: React.FC<LogoLoaderProps> = ({
  label = 'Loading…',
  logoUrl,
}) => {
  const theme = useTheme()
  const markColor = resolveMarkColor(theme.palette)

  /**
   * Which URL failed, rather than a `hasFailed` boolean: a boolean would latch, so a
   * theme that re-resolved to a working logo after a broken one would stay on the V.
   *
   * The fallback is keyed on the LOAD FAILING, not just on the URL being absent. A
   * brand logo is a remote asset on someone else's bucket — it can 404, be blocked, or
   * be swapped for a bad URL in Studio — and a loader is the one screen where an empty
   * hole is unrecoverable, because there is nothing else on it to read.
   */
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  const showBrandLogo = !!logoUrl && logoUrl !== failedUrl

  return (
  <Box
    role="status"
    aria-label="Loading"
    sx={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      backgroundColor: '#fff',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '20px',
    }}
  >
    <Box sx={{ position: 'relative', width: 64, height: 64 }}>
      <Box
        component="svg"
        viewBox="0 0 48 48"
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          width: 64,
          height: 64,
          animation: `${spin} 1.4s linear infinite`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      >
        <Box
          component="circle"
          cx="24"
          cy="24"
          r="21"
          sx={{
            fill: 'none',
            stroke: markColor,
            strokeWidth: 2,
            strokeLinecap: 'round',
            strokeDasharray: 132,
            opacity: 0.9,
            animation: `${dash} 1.4s ease-in-out infinite`,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        />
      </Box>
      {showBrandLogo ? (
        <Box
          component="img"
          src={logoUrl}
          alt=""
          aria-hidden="true"
          /* A brand logo is any aspect ratio — a square monogram, a wide wordmark, a
             tall stacked lockup. `contain` letterboxes it inside the mark's box so it
             is never stretched and never overflows the ring, which is the same fit
             loader.css gives the V (`center / contain`). */
          onError={() => setFailedUrl(logoUrl ?? null)}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            ...MARK_BOX,
            transform: 'translate(-50%, -50%)',
            objectFit: 'contain',
          }}
        />
      ) : (
        <Box
          component="svg"
          viewBox="0 0 172 148"
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            ...MARK_BOX,
            transform: 'translate(-50%, -50%)',
            '& path': { fill: markColor },
          }}
        >
          <path d={VERA_MARK_PATH} />
        </Box>
      )}
    </Box>
    <Box
      sx={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        fontSize: '13px',
        letterSpacing: '0.02em',
        color: '#6b6b6b',
        opacity: 0,
        animation: `${fadeIn} 0.3s ease-out 1.2s forwards`,
      }}
    >
      {label}
    </Box>
    {/*
      VRG-529 — rendered UNGATED, and that is deliberate rather than an oversight.

      `hide_powered_by` is the Enterprise white-label entitlement, and it rides the
      CERTIFICATE payload. At this point in the load there is no certificate: the app
      is still resolving auth and the company theme, which is the entire reason this
      component is on screen. So the entitlement is not merely unread here, it is
      unreadable — and the only way to honour it would be for the style endpoint
      (`GET /companies/public/{id}/settings/style`, the same response that already
      carries `styles.logo`) to start returning `hide_powered_by`. It does not today.

      `OnboardingShell` and the `ClaimOwnershipSignUp` unusable-link layout render it
      the same way, for the same reason, and the second carries the same note.

      No local `hidden={...}` check: `PoweredByVera` refuses to render itself when the
      entitlement applies (see its docblock), and a per-call-site condition is the exact
      bug that component was extracted to end. When the endpoint does carry the flag,
      the fix is to feed it to the component, not to add a branch here.
    */}
    <Box
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        // Absolute px, not a spacing factor: `apps/app` runs MUI's default 8px base
        // while `@repo/ui`/platform run 4px (working-conventions §29), so a factor
        // copied between them renders at double size. The safe-area inset keeps it
        // clear of the iOS home indicator — this is a full-viewport fixed overlay on
        // a phone, which is the only device the app is used on.
        bottom: 'max(24px, env(safe-area-inset-bottom))',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <PoweredByVera />
    </Box>
  </Box>
  )
}
